import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLogin } from "@/hooks/useLogin";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2, X } from "lucide-react";

const APP_NAME = import.meta.env.VITE_APP_NAME || "Hospital Management System";

export default function LoginPage() {
  const { login, loading, error, setError } = useLogin();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getRoleRoute = (role: string): string => {
    const roleRoutes: Record<string, string> = {
      doctor: "/",
      staff: "/frontdesk",
      admin: "/settings",
    };
    return roleRoutes[role] || "/";
  };

  useEffect(() => {
    const prev = document.title;
    document.title = `Sign in | ${APP_NAME}`;
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (error && emailInputRef.current) {
      emailInputRef.current?.focus();
    }
  }, [error]);

  useEffect(() => {
    if (currentUser) {
      navigate(getRoleRoute(currentUser.role), { replace: true });
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    setIsSubmitting(true);
    try {
      await login(email, password, rememberMe);
    } catch (err) {
      console.error("Login failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormBusy = loading || isSubmitting;

  if (currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: Branding panel */}
      <div className="hidden lg:flex lg:w-[44%] flex-col justify-between bg-gradient-to-br from-primary via-primary/95 to-primary/85 p-8 xl:p-12 text-primary-foreground relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative z-10">
          <img
            src="/onecheckup-logo-primary.png"
            alt={APP_NAME}
            className="w-60 xl:w-72 object-contain brightness-0 invert mb-12"
          />

          <h2 className="text-3xl xl:text-4xl font-bold leading-tight max-w-sm mb-4">
            More patients.{"\n"}
            Less paperwork.
          </h2>
          <p className="text-white/80 text-sm max-w-sm leading-relaxed">
            OneCheckUp helps clinics across India manage
            queues, prescriptions, and billing —
            all from one screen.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-6">
          <div>
            <p className="text-white text-lg font-bold">50+</p>
            <p className="text-white/60 text-xs">Clinics</p>
          </div>
          <div className="w-px h-8 bg-white/20" />
          <div>
            <p className="text-white text-lg font-bold">1000+</p>
            <p className="text-white/60 text-xs">Patients served</p>
          </div>
          <div className="w-px h-8 bg-white/20" />
          <div>
            <p className="text-white text-lg font-bold">India</p>
            <p className="text-white/60 text-xs">& growing</p>
          </div>
        </div>
      </div>

      {/* Right: Sign-in form */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-8 sm:px-6 lg:px-8 bg-background">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/onecheckup-logo-primary.png" alt={APP_NAME} className="w-56 object-contain" />
          </div>

          <div className="lg:rounded-xl lg:border lg:border-border/50 lg:border-l-4 lg:border-l-primary lg:p-8 lg:shadow-sm">
            <h1 className="text-2xl font-bold text-foreground mb-1">Sign in</h1>
            <p className="text-muted-foreground text-sm mb-6">
              Welcome back! Please enter your details.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-foreground text-sm font-medium">
                  Email
                </Label>
                <Input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  disabled={isFormBusy}
                  required
                  autoComplete="email"
                  className={`h-11 rounded-lg border-2 bg-background text-sm transition-colors ${
                    error 
                      ? 'border-destructive/50 focus:border-destructive focus:ring-destructive/20' 
                      : 'border-input focus:border-ring'
                  }`}
                  aria-describedby={error ? "login-error" : undefined}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-foreground text-sm font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  disabled={isFormBusy}
                  required
                  autoComplete="current-password"
                  className={`h-11 rounded-lg border-2 bg-background text-sm transition-colors ${
                    error 
                      ? 'border-destructive/50 focus:border-destructive focus:ring-destructive/20' 
                      : 'border-input focus:border-ring'
                  }`}
                  aria-describedby={error ? "login-error" : undefined}
                />
                <div className="flex items-center justify-end gap-3 pt-0.5">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isFormBusy}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? "Hide password" : "Show password"}
                  </button>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                  disabled={isFormBusy}
                  className="rounded"
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-normal text-muted-foreground cursor-pointer select-none"
                >
                  Remember me
                </Label>
              </div>

              {error && (
                <Alert 
                  id="login-error" 
                  variant="destructive" 
                  className="py-3 px-4 text-sm border-destructive/50 bg-destructive/5 animate-in fade-in slide-in-from-top-2 duration-300" 
                  role="alert"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <AlertDescription className="text-destructive font-medium">
                        {error === "Invalid email or password" 
                          ? "Incorrect email or password. Please check and try again." 
                          : error}
                      </AlertDescription>
                    </div>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      className="flex-shrink-0 text-destructive/70 hover:text-destructive transition-colors"
                      aria-label="Dismiss error"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={isFormBusy}
                className="w-full h-11 rounded-lg font-semibold text-sm mt-2 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isFormBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>

            <p className="mt-5 text-xs text-center text-muted-foreground">
              New clinic?{" "}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSfOP9MUaGkdlKZNuD6Off1BYDz_ickWmEMVH4hOhqDkJz2GWA/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-semibold hover:underline"
              >
                Contact us
              </a>
            </p>
          </div>

          <p className="mt-6 text-[11px] text-muted-foreground text-center leading-relaxed">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
